import type { Feature, Geometry } from 'geojson';
import { dataTypeToFeatureType} from '../common/Constants';
import GeoLocation from './GeoLocation';
import type { LngLat } from 'mapbox-gl';
import type CardData from './CardData';

/** Map feature this data can be built from. */
export type MapFeature = Feature<Geometry>;

/**
 * Object to store feature data.
 *
 * Subclasses:
 *
 * - PlaceData
 *   - CityData
 *   - HotelData
 * - TrackData
 *   - RouteData
 *   - StageData
 */
class FeatureData {
    // Object / data type.
    type: string;
    // 0 for missing parameter
    id: number;
    name?: string;

    // Rest URL
    restUrl?: string;
    // Link URL
    linkUrl?: string;

    // Map Feature
    mapFeature?: MapFeature;

    /**
     * Optional place location.
     */
    location?: GeoLocation;

    // Coordenadas geograficas del punto que origino la seleccion.
    // @see MapComponent.handleFeatureClick
    clickPoint?: LngLat;

    // DOM element that represents this feature on the map.
    htmlElement?: HTMLElement | null;

    constructor(type: string, id: number | string = 0) {
      this.type = type;
      this.id = parseInt(String(id));
    }

    /**
     * Gets (mapbox) item id.
     *
     * Abstract in practice: CityData, HotelData, POIData, RouteData and
     * StageData override it. It is not declared `abstract` because that would
     * remove the method from the prototype, and PlaceData is instantiated
     * directly (@see GeoService.placeFromUrl) relying on the undefined below.
     */
    getMapItemId(): string | undefined {
      return undefined;
    }

    /**
     * Convert to list (for destructuring).
     *
     * Usage: [lon, lat, zoom] = this.toList();
     */
    toList() {
      return [this.type, this.id];
    }

    /**
     * Gets location.
     */
    getGeoLocation(): GeoLocation | undefined {
      return this.location;
    }

    /**
     * Set data from feature.
     */
    setFeature(feature: MapFeature): void {
      this.mapFeature = feature;

      if (feature.properties && feature.properties.name) {
        this.name = feature.properties.name;
      }
      // For feature type 'Point', geometry.coordinates is an array with lon, lat.
      // For feature type 'LineString', geometry.coordinates is an array of points.
      if (feature.geometry && feature.geometry.type === 'Point' && feature.geometry.coordinates) {
        const [lon, lat] = feature.geometry.coordinates;
        this.location = new GeoLocation(lon, lat);
      }
    }

    /**
     * Check whether this comes from a map feature.
     */
    isMapFeature(): boolean {
      return !!this.mapFeature;
    }

    /**
     * Gets card type / feature type.
     */
    getFeatureType() {
      return dataTypeToFeatureType(this.type);
    }

    /**
     * Create DOM Element
     */
    createElement(): HTMLElement {
        if (!this.htmlElement) {
          const el = document.createElement('div');
          // getMapItemId() puede devolver undefined. Asignar undefined a el.id
          // ya guardaba la cadena "undefined"; String() lo hace explicito sin
          // cambiar el resultado.
          el.id = String(this.getMapItemId());
          this.htmlElement = el;
        }

        return this.htmlElement;
    }

    /**
     * Gets HTML element from document
     */
    getMapElement(refresh = true): HTMLElement | null | undefined {
        if (refresh || !this.htmlElement) {
          this.htmlElement = document.getElementById(String(this.getMapItemId()));
        }
        return this.htmlElement;
    }

    /**
     * Check whether the referenced object maps a Card.
     *
     * Devuelve un valor veraz/falso, no un boolean estricto: `this.id` puede ser
     * 0 (el valor por defecto del constructor) y getMapItemId() puede devolver
     * "". Se deja inferir el tipo en vez de declarar uno mas estrecho que el
     * comportamiento real.
     */
    matchCardData(card: CardData) {
      // Default implementation if loaded from list and match
      return this.type === card.type &&
      ((this.id && this.id === card.id) || this.getMapItemId() === card.getMapId());
    }

    /**
     * Check whether the referenced object maps another Feature data.
     *
     * @see matchCardData sobre el tipo de retorno.
     */
    matchFeatureData(feature: FeatureData) {
      // Default implementation if loaded from list and match
      return this.type === feature.type && (
       (this.id && this.id === feature.id) ||
       (this.getMapItemId() && this.getMapItemId() === feature.getMapItemId())
      );
    }
}

export default FeatureData;
